//Common
import {
  FORMA_RATEIO,
  SITUACAO_PAGAMENTO,
  TIPO_CENTRO_CUSTO,
  TIPO_RATEIO,
} from "@/common/constantes";
import {
  fn_calcularRateio,
  fn_calcularRateioAutomatico,
  fn_getLabelCentroCusto,
} from "@/common/siig";
import { fn_calcularTotal, fn_isEmpty } from "@/common/utils/util";

//Util
import { ICONES_MENSAGEM } from "../icones";
import { MENSAGENS_ERRO, showMensagemErro } from "../mensagens";

//Retorna os distintos centros dos rateios dos pagamentos
export function fn_getCentrosRateioPagamentos(
  pagamentos,
  somenteComValor = true,
) {
  let rateiosPagamentos = pagamentos.map((pag) => pag.rateio).flat();

  if (somenteComValor) {
    rateiosPagamentos = rateiosPagamentos.filter(
      (rateio) => Number(rateio.valor) > 0,
    );
  }

  const centrosRateio = [
    ...new Map(
      rateiosPagamentos.map((rateio) => {
        const centro = rateio.centro_custo;
        return [centro.value, centro];
      }),
    ).values(),
  ];

  return centrosRateio;
}

//Ajusta o rateio do valor documento às contas da solicitação (correção com inclusão/exclusão de conta):
//retira as contas que saíram e inclui, com valor zero, as contas que entraram. As demais ficam como estão.
export function fn_ajustarContasRateio(rateio, contas) {
  const idsContas = contas.map((conta) => String(conta.value));

  const valoresContas = {};
  idsContas.forEach((idConta) => {
    valoresContas[idConta] = Number(rateio?.valores_contas?.[idConta] || 0);
  });

  const rateioCentrosConta = {};
  Object.entries(rateio?.rateio_centros_conta || {}).forEach(
    ([idConta, rateioConta]) => {
      if (idsContas.includes(String(idConta))) {
        rateioCentrosConta[idConta] = rateioConta;
      }
    },
  );

  return {
    ...rateio,
    valores_contas: valoresContas,
    valor_total_contas: fn_calcularTotal(valoresContas),
    rateio_centros_conta: rateioCentrosConta,
  };
}

//Verifica se a conta está com o rateio por centro de custo diferente do valor dela (o valor da conta mudou)
function contaComRateioCentroDesatualizado(rateio, idConta) {
  const rateioConta = rateio?.rateio_centros_conta?.[idConta];
  const valorConta = Number(rateio?.valores_contas?.[idConta] || 0);
  const valorTotalCentros = Number(rateioConta?.valor_total_centros || 0);

  return (
    valorConta > 0 &&
    valorTotalCentros > 0 &&
    valorConta.toFixed(2) != valorTotalCentros.toFixed(2)
  );
}

//Verifica se o rateio manual do valor documento possui alguma conta com o rateio por centro de custo
//diferente do valor da conta (Ex: a conta recebeu o valor de uma conta excluída)
export function fn_rateioPossuiCentroDesatualizado(rateio) {
  return Object.keys(rateio?.valores_contas || {}).some((idConta) =>
    contaComRateioCentroDesatualizado(rateio, idConta),
  );
}

/*
 * Recalcula o rateio MANUAL por centro de custo das contas do valor documento que mudaram de valor,
 * mantendo a proporção que cada centro já tinha:
 *  - rateio por QUANTIDADE: conforme as quantidades informadas em cada centro
 *  - rateio por VALOR: conforme o percentual que o valor de cada centro representava
 * A conta sem rateio por centro informado (ou com o rateio já correto) fica como está.
 */
export function fn_recalcularRateioManualCentros(
  rateio,
  formaRateio,
  selectedCentros,
) {
  const rateioCentrosConta = { ...(rateio?.rateio_centros_conta || {}) };

  Object.keys(rateio?.valores_contas || {}).forEach((idConta) => {
    if (!contaComRateioCentroDesatualizado(rateio, idConta)) {
      return;
    }

    const rateioConta = rateioCentrosConta[idConta];
    const valorConta = Number(rateio.valores_contas[idConta]);

    const valoresCentros = fn_calcularRateio(
      selectedCentros,
      formaRateio == FORMA_RATEIO.QUANTIDADE
        ? rateioConta.qtdes_centros || {}
        : rateioConta.valores_centros || {},
      valorConta,
    );

    //Sem base para a proporção (Ex: sem quantidade informada), não recalcula
    if (fn_isEmpty(valoresCentros)) {
      return;
    }

    rateioCentrosConta[idConta] = {
      ...rateioConta,
      valor_conta: valorConta,
      valores_centros: valoresCentros,
      //Arredonda para não ficar com resíduo de ponto flutuante (Ex: 999.9999999999999)
      valor_total_centros: Number(fn_calcularTotal(valoresCentros).toFixed(2)),
    };
  });

  return { ...rateio, rateio_centros_conta: rateioCentrosConta };
}

//Retorna o objeto rateio quando existe apenas uma conta
export function fn_getRateioPagamento(
  pagamento,
  valorRateioDoc,
  valoresRateioPorTipo,
  selectedContas,
  selectedCentros,
) {
  let rateio = pagamento.rateio;

  //Se recebeu a informação do rateio automático, utiliza, senão não calcula
  const infRateioAutomatico = pagamento.inf_rateio_automatico || {
    mes_ano: null,
    qtdes_colab_centro: {},
    fat_filial: {},
  };

  //1 - RATEIO DO VALOR DOCUMENTO
  //Se selecionou só uma conta
  if (selectedContas.length == 1) {
    const idConta = selectedContas[0].value;
    const valoresContas = { [idConta]: valorRateioDoc };

    let rateioCentrosConta = rateio?.rateio_centros_conta || {};

    //Se for rateio AUTOMATICO
    if (pagamento.tipo_rateio == TIPO_RATEIO.AUTOMATICO) {
      rateioCentrosConta = fn_calcularRateioAutomatico(
        pagamento.forma_rateio,
        infRateioAutomatico,
        selectedCentros,
        null,
        valoresContas,
      );
    }
    //Se for rateio MANUAL
    else {
      //Se selecinou só um centro de custo, seta o rateio do centro com o valor da conta
      if (selectedCentros.length == 1) {
        rateioCentrosConta = {
          [idConta]: {
            id_conta: idConta,
            valor_conta: valorRateioDoc,
            qtdes_centros: {},
            valores_centros: {
              [selectedCentros[0].value]: valorRateioDoc,
            },
            valor_total_centros: valorRateioDoc,
          },
        };
      }
    }

    rateio = {
      ...rateio,
      valores_contas: valoresContas,
      valor_total_contas: valorRateioDoc,
      rateio_centros_conta: rateioCentrosConta,
    };
  }
  //Se selecionou mais de uma conta
  else if (selectedContas.length > 1) {
    let rateioCentrosConta = rateio.rateio_centros_conta;

    //Se for rateio AUTOMATICO
    if (pagamento.tipo_rateio == TIPO_RATEIO.AUTOMATICO) {
      rateioCentrosConta = fn_calcularRateioAutomatico(
        pagamento.forma_rateio,
        infRateioAutomatico,
        selectedCentros,
        null,
        rateio.valores_contas,
      );
    }
    //Se for rateio MANUAL
    else {
      //Se selecinou só um centro de custo, seta o rateio do centro com o valor de cada conta
      if (selectedCentros.length == 1) {
        Object.entries(rateio?.valores_contas || {}).forEach(
          ([idConta, valorConta]) => {
            rateioCentrosConta[idConta] = {
              id_conta: idConta,
              valor_conta: valorConta,
              qtdes_centros: {},
              valores_centros: {
                [selectedCentros[0].value]: valorConta,
              },
              valor_total_centros: valorConta,
            };
          },
        );
      }
    }

    rateio = {
      ...rateio,
      rateio_centros_conta: rateioCentrosConta,
    };
  }

  //Rateio MANUAL por QUANTIDADE com mais de um centro: a conta que mudou de valor (Ex: alteração do valor documento)
  //tem os centros recalculados conforme as quantidades informadas, como no rateio automático.
  //Só recalcula a conta com o total dos centros diferente do valor dela (o rateio que está certo fica como está)
  if (
    pagamento.tipo_rateio == TIPO_RATEIO.MANUAL &&
    pagamento.forma_rateio == FORMA_RATEIO.QUANTIDADE &&
    selectedCentros.length > 1
  ) {
    rateio = fn_recalcularRateioManualCentros(
      rateio,
      FORMA_RATEIO.QUANTIDADE,
      selectedCentros,
    );
  }

  //2 - RATEIO DOS VALORES POR TIPO (Ex: Desconto, Despesa, Juros, Multa)
  Object.entries(valoresRateioPorTipo).forEach(([tipo, valoresContas]) => {
    if (selectedCentros.length == 1) {
      Object.entries(valoresContas).forEach(([idConta, valor]) => {
        rateio = {
          ...rateio,
          rateio_centros_conta_tp_valor: {
            ...(rateio?.rateio_centros_conta_tp_valor || {}),
            [idConta]: {
              id_conta: idConta,
              valor_conta: valor,
              tipo_valor_conta: tipo,
              valores_centros: {
                [selectedCentros[0].value]: valor,
              },
              valor_total_centros: valor,
            },
          },
        };
      });
    }
    //Se for rateio AUTOMATICO
    else if (pagamento.tipo_rateio == TIPO_RATEIO.AUTOMATICO) {
      rateio = {
        ...rateio,
        rateio_centros_conta_tp_valor: {
          ...(rateio?.rateio_centros_conta_tp_valor || {}),
          ...fn_calcularRateioAutomatico(
            pagamento.forma_rateio,
            infRateioAutomatico,
            selectedCentros,
            tipo,
            valoresContas,
          ),
        },
      };
    }
    //Se for rateio MANUAL por QUANTIDADE: a conta do tipo que mudou de valor tem os centros recalculados
    //conforme as quantidades informadas (mesma regra do valor documento)
    else if (
      pagamento.tipo_rateio == TIPO_RATEIO.MANUAL &&
      pagamento.forma_rateio == FORMA_RATEIO.QUANTIDADE
    ) {
      Object.entries(valoresContas).forEach(([idConta, valor]) => {
        const rateioContaTipo = recalcularRateioContaPorQuantidade(
          rateio?.rateio_centros_conta_tp_valor?.[idConta],
          valor,
          selectedCentros,
        );

        if (rateioContaTipo) {
          rateio = {
            ...rateio,
            rateio_centros_conta_tp_valor: {
              ...(rateio?.rateio_centros_conta_tp_valor || {}),
              [idConta]: rateioContaTipo,
            },
          };
        }
      });
    }
  });

  return rateio;
}

//Recalcula o rateio manual por quantidade de uma conta (valor por tipo) que mudou de valor.
//Retorna null quando não precisa recalcular: sem rateio por centro informado, valor zerado,
//rateio já correto ou sem quantidade informada nos centros
function recalcularRateioContaPorQuantidade(
  rateioConta,
  valorConta,
  selectedCentros,
) {
  const valor = Number(valorConta || 0);
  const valorTotalCentros = Number(rateioConta?.valor_total_centros || 0);

  if (
    !rateioConta ||
    valor <= 0 ||
    valorTotalCentros <= 0 ||
    valor.toFixed(2) == valorTotalCentros.toFixed(2)
  ) {
    return null;
  }

  const valoresCentros = fn_calcularRateio(
    selectedCentros,
    rateioConta.qtdes_centros || {},
    valor,
  );

  //Sem base para a proporção (Ex: sem quantidade informada), não recalcula
  if (fn_isEmpty(valoresCentros)) {
    return null;
  }

  return {
    ...rateioConta,
    valor_conta: valor,
    valores_centros: valoresCentros,
    //Arredonda para não ficar com resíduo de ponto flutuante (Ex: 999.9999999999999)
    valor_total_centros: Number(fn_calcularTotal(valoresCentros).toFixed(2)),
  };
}

//Obtém a situação do rateio de determinado pagamento: P=Pendente; F=Finalizado; E=Erro;
export function fn_getSituacaoRateioPagamento(
  pagamento,
  valorRateioDoc,
  valoresRateioTipo,
  selectedCentros = [],
) {
  //1 - Obtém a situação do rateio do valor documento (contas selecionadas na aba Identificação)
  const situacaoRateioValDoc = fn_getSituacaoRateioValDocumento(
    pagamento,
    valorRateioDoc,
    selectedCentros,
  );

  //2 - Obtém a situação do rateio dos valores por tipo (contas de desconto, despesa, juros, multa)
  const situacaoRateioTpValor = fn_getSituacaoRateioTipoValor(
    pagamento,
    valoresRateioTipo,
    selectedCentros,
  );

  let situacaoRet = "P";

  if (situacaoRateioValDoc == "E" || situacaoRateioTpValor == "E")
    situacaoRet = "E";
  else if (situacaoRateioValDoc == "P" || situacaoRateioTpValor == "P") {
    situacaoRet = "P";
  } else if (situacaoRateioValDoc == "F" && situacaoRateioTpValor == "F") {
    situacaoRet = "F";
  }

  return situacaoRet;
}

//Obtém a situação do rateio do valor documento de determinada parcela
function fn_getSituacaoRateioValDocumento(
  pagamento,
  valorRateioDoc,
  selectedCentros = [],
) {
  const valorTotalContas = Number(pagamento?.rateio?.valor_total_contas || 0);

  let contaIsPend = false;
  let centroIsErro = false;
  let centroIsPend = false;

  //Verifica se alguma conta informada está sem valor
  Object.entries(pagamento?.rateio?.valores_contas || {}).forEach(
    ([_, valorConta]) => {
      if (Number(valorConta || 0) == 0) {
        contaIsPend = true;
      }
    },
  );

  //Verifica se já foi informado o rateio por centro custo
  const rateioCentrosConta = pagamento?.rateio?.rateio_centros_conta || {};

  if (fn_isEmpty(rateioCentrosConta)) {
    centroIsPend = true;
  } else if (Object.values(rateioCentrosConta).length > 0) {
    Object.entries(rateioCentrosConta).forEach(([idConta, rateioConta]) => {
      const valorConta = Number(pagamento.rateio.valores_contas[idConta] || 0);

      if (valorConta > 0) {
        const valorTotalCentro = Number(rateioConta.valor_total_centros || 0);

        if (valorTotalCentro == 0) {
          centroIsPend = true;
        } else if (valorConta.toFixed(2) != valorTotalCentro.toFixed(2)) {
          centroIsErro = true;
        }

        //Verifica se o rateio possui centro de custo inativo com valor
        if (
          fn_rateioPossuiCentroInativoComValor(
            rateioConta,
            selectedCentros,
            pagamento.tipo_rateio,
            pagamento.situacao,
          )
        ) {
          centroIsErro = true;
        }
      }
    });
  } else {
    centroIsPend = true;
  }

  if (valorTotalContas > 0) {
    //Se o valor total da conta for diferente do valor do rateio ou houver centro com erro
    if (
      valorTotalContas.toFixed(2) != valorRateioDoc.toFixed(2) ||
      centroIsErro
    ) {
      return "E"; //Erro (contas ou centro custo)
    }
    //Se tiver conta ou centro pendente
    else if (contaIsPend || centroIsPend) {
      return "P"; //Pendente (centro custo)
    }
    //Se os valor total das contas for igual ao valor do rateio
    else if (valorTotalContas.toFixed(2) == valorRateioDoc.toFixed(2)) {
      return "F"; //Finalizado (contas ou centro custo)
    }
  } else {
    return "P"; //Pendente (contas)
  }
}

//Obtém a situação do rateio para os tipos de valor (Desconto, Despesa, Juros, Multa) de determinada parcela
function fn_getSituacaoRateioTipoValor(
  pagamento,
  valoresRateioTipo,
  selectedCentros = [],
) {
  let centroIsErro = false;
  let centroIsPend = false;

  const rateioCentrosConta =
    pagamento?.rateio?.rateio_centros_conta_tp_valor || {};

  //Para cada tipo valor (Desconto, Despesa, Juros, Multa...)
  Object.entries(valoresRateioTipo || {}).forEach(
    ([tipoConta, valoresConta]) => {
      Object.entries(valoresConta).forEach(([idConta, valorConta]) => {
        //Se informou valor na conta do tipo, verifica se o rateio foi informado
        if (Number(valorConta) > 0) {
          const rateioConta = rateioCentrosConta[idConta];

          if (!fn_isEmpty(rateioConta)) {
            const valorTotalCentro = Number(
              rateioConta.valor_total_centros || 0,
            );

            //Se o valor total do centros for zero
            if (valorTotalCentro == 0) {
              centroIsPend = true;
            }
            //Se o valor total dos centros for diferente do valor da conta
            else if (
              Number(valorConta).toFixed(2) != valorTotalCentro.toFixed(2)
            ) {
              centroIsErro = true;
            }

            //Verifica se o rateio possui centro de custo inativo com valor
            if (
              fn_rateioPossuiCentroInativoComValor(
                rateioConta,
                selectedCentros,
                pagamento.tipo_rateio,
                pagamento.situacao,
              )
            ) {
              centroIsErro = true;
            }
          } else {
            centroIsPend = true;
          }
        }
      });
    },
  );

  if (centroIsErro) {
    return "E";
  } else if (centroIsPend) {
    return "P";
  } else {
    return "F";
  }
}

//Obtém a situação do rateio de todas as contas de determinado tipo: Valor doc, Desconto, Despesa, Multa, Juros...
export function fn_getSituacaoRateioContas(
  tipoRateio,
  situacaoPagamento,
  rateioCentrosConta,
  valoresConta = {},
  selectedCentros = [],
) {
  let centroIsErro = false;
  let centroIsPend = false;

  //Para cada conta
  Object.entries(valoresConta).forEach(([idConta, valorConta]) => {
    //Se informou valor da conta, verifica se o rateio foi informado
    if (Number(valorConta) > 0) {
      const rateioConta = rateioCentrosConta[idConta] || {};

      if (!fn_isEmpty(rateioConta)) {
        const valorTotalCentro = Number(rateioConta.valor_total_centros || 0);

        //Se o valor total do centros for zero
        if (valorTotalCentro == 0) {
          centroIsPend = true;
        }
        //Se o valor total dos centros for diferente do valor da conta
        else if (
          Number(valorConta.toFixed(2)) != Number(valorTotalCentro.toFixed(2))
        ) {
          centroIsErro = true;
        }

        //Verifica se o rateio possui centro de custo inativo com valor
        if (
          fn_rateioPossuiCentroInativoComValor(
            rateioConta,
            selectedCentros,
            tipoRateio,
            situacaoPagamento,
          )
        ) {
          centroIsErro = true;
        }
      } else {
        centroIsPend = true;
      }
    }
  });

  if (centroIsErro) {
    return "E";
  } else if (centroIsPend) {
    return "P";
  } else {
    return "F";
  }
}

//Retorna o jsx com a apresentação do ícone do rateio de determinada conta
export function fn_getIconeSituacaoRateioConta(
  tipoRateio,
  situacaoPagamento,
  rateioConta = {},
  valorConta = 0,
  selectedCentros,
) {
  let centroIsErro = false;
  let centroIsPend = false;
  let msgErro = "";

  if (!fn_isEmpty(rateioConta)) {
    const valorTotalCentro = Number(rateioConta.valor_total_centros || 0);

    if (valorTotalCentro == 0) {
      centroIsPend = true;
    } else if (
      Number(valorConta.toFixed(2)) != Number(valorTotalCentro.toFixed(2))
    ) {
      centroIsErro = true;
      msgErro = "Total rateio incorreto!";
    }

    //Verifica se o rateio possui centro de custo inativo com valor
    if (
      fn_rateioPossuiCentroInativoComValor(
        rateioConta,
        selectedCentros,
        tipoRateio,
        situacaoPagamento,
      )
    ) {
      centroIsErro = true;
      msgErro = "Centro de custo inativo!";
    }
  } else {
    centroIsPend = true;
  }

  if (centroIsErro) {
    return fn_getIconeSituacaoRateio("E", msgErro);
  } else if (centroIsPend) {
    return fn_getIconeSituacaoRateio("P");
  } else {
    return fn_getIconeSituacaoRateio("F");
  }
}

//Retorna o ícone da situação do rateio
export function fn_getIconeSituacaoRateio(situacao, msgErro) {
  //Se rateio finalizado
  if (situacao == "F") {
    return <span className="ms-1 text-success">{ICONES_MENSAGEM.OK}</span>;
  }
  //Se rateio com erro
  else if (situacao == "E") {
    return (
      <span className="ms-1 text-danger" title={msgErro || "Rateio incorreto!"}>
        {ICONES_MENSAGEM.ERRO}
      </span>
    );
  }
  //Senão
  else {
    return (
      <span className="ms-1 text-danger" title="Rateio não informado!">
        {ICONES_MENSAGEM.ATENCAO}
      </span>
    );
  }
}

//Verifica se o rateio possui algum centro inativo com valor
export function fn_rateioPossuiCentroInativoComValor(
  rateioConta,
  selectedCentros,
  tipoRateio,
  situacaoPagamento,
) {
  let possui = false;

  if (
    // tipoRateio != TIPO_RATEIO.AUTOMATICO &&
    situacaoPagamento != SITUACAO_PAGAMENTO.CONFERIDO
  ) {
    //Obtém os centros inativos que estão no rateio
    const centrosInativos = selectedCentros.filter(
      (opCentro) => opCentro.situacao == "I",
    );

    //Verifica se algum centro inativo está com valor
    centrosInativos.forEach((opCentro) => {
      if (Number(rateioConta.valores_centros[opCentro.value] || 0) > 0) {
        possui = true;
      }
    });
  }
  return possui;
}

//Retorna um array com os centros de custos do rateio que estão inativos com valor
export function fn_getCentrosInativosComValor(rateio, selectedCentros) {
  const map = new Map();

  const rateiosCentrosConta = {
    ...(rateio?.rateio_centros_conta || {}),
    ...(rateio?.rateio_centros_conta_tp_valor || {}),
  };

  //Obtém os centros inativos que estão no rateio
  const centrosInativos = selectedCentros.filter(
    (opCentro) => opCentro.situacao == "I",
  );

  //Verifica se algum centro inativo está com valor
  centrosInativos.forEach((opCentro) => {
    const idCentro = opCentro.value;

    const existeComValor = Object.values(rateiosCentrosConta).some((conta) => {
      const valor = conta?.valores_centros?.[idCentro];

      return valor > 0;
    });

    if (existeComValor) {
      map.set(idCentro, opCentro);
    }
  });

  return Array.from(map.values());
}

//Retorna os rateios que estão com valor zero no centro custo
export function fn_getRateiosZero(rateio, selectedCentros) {
  const rateios = [];

  const rateiosCentrosConta = {
    ...(rateio?.rateio_centros_conta || {}),
    ...(rateio?.rateio_centros_conta_tp_valor || {}),
  };

  //Obtém os centros ativos do rateio
  const centrosAtivos = selectedCentros.filter(
    (opCentro) => opCentro.situacao == "A",
  );

  for (const [idConta, objRateio] of Object.entries(rateiosCentrosConta)) {
    if (Number(objRateio.valor_conta || 0) > 0) {
      const valoresCentros = objRateio?.valores_centros || {};

      centrosAtivos.forEach((centro) => {
        const valorCentro = valoresCentros[centro.value];

        if (Number(valorCentro || 0) === 0) {
          rateios.push({
            id_conta: idConta,
            id_centro: centro.value,
          });
        }
      });
    }
  }

  return rateios;
}

//Agrupa os rateios por id conta + id centro custo
export function fn_agruparRateioContaCentro(rateios) {
  const agrupado = {};

  for (const r of rateios) {
    const key = `${r.id_conta}-${r.id_centro_custo}`;

    if (!agrupado[key]) {
      agrupado[key] = {
        tipo_valor_conta: r.tipo_valor_conta,
        conta: r.conta,
        centro_custo: r.centro_custo,
        valor: 0,
        quantidade: 0,
      };
    }

    agrupado[key].valor += Number(r.valor || 0);
    agrupado[key].quantidade += Number(r.quantidade || 0);
  }

  return Object.values(agrupado).sort((a, b) =>
    (b.tipo_valor_conta + "-" + b.conta.id).localeCompare(
      a.tipo_valor_conta + "-" + a.conta.id,
    ),
  );
}

//Agrupa os rateios por id conta
export function fn_agruparRateioConta(rateios) {
  const agrupado = {};

  for (const r of rateios) {
    const key = `${r.id_conta}`;

    if (!agrupado[key]) {
      agrupado[key] = {
        tipo_valor_conta: r.tipo_valor_conta,
        conta: r.conta,
        valor: 0,
        quantidade: 0,
      };
    }

    agrupado[key].valor += Number(r.valor || 0);
    agrupado[key].quantidade += Number(r.quantidade || 0);
  }

  return Object.values(agrupado).sort((a, b) =>
    (b.tipo_valor_conta + "-" + b.conta.id).localeCompare(
      a.tipo_valor_conta + "-" + a.conta.id,
    ),
  );
}

//Verifica se o pagamento 1 possui os mesmos valores do pagamento 2 para aplicar o rateio
//Valores que afetam rateio: Documento, Desconto, Despesa, Multa e Juros
export function fn_pagMesmoValorParaAplicarRateio(pag1, pag2) {
  return Number(pag1.valor_documento) == Number(pag2.valor_documento) &&
    Number(pag1.valor_desconto) == Number(pag2.valor_desconto) &&
    Number(pag1.valor_despesa) == Number(pag2.valor_despesa) &&
    Number(pag1.valor_multa) == Number(pag2.valor_multa) &&
    Number(pag1.valor_juros) == Number(pag2.valor_juros)
    ? true
    : false;
}

//Carrega os dados do rateio para edição
export function fn_carregarRateioEdicao(pagamento) {
  //Rateio valor doc é só de uma conta
  const rateioValorDoc = pagamento.rateio.filter(
    (rateio) => rateio.tipo_valor_conta == null,
  );

  //Rateio tipo valor pode ter zero ou muitas contas
  const rateioTipoValor = pagamento.rateio.filter(
    (rateio) => rateio.tipo_valor_conta != null,
  );

  const valoresContas = rateioValorDoc.reduce((acc, item) => {
    acc[item.id_conta] = Number(item.valor_conta);
    return acc;
  }, {});

  //Preenche o objeto rateio
  const rateioCentroConta = carregarRateioCentroEdicao(rateioValorDoc);
  const rateioCentroContaTpValor = carregarRateioCentroEdicao(rateioTipoValor);

  return {
    valores_contas: valoresContas || {},
    valor_total_contas: fn_calcularTotal(valoresContas),
    rateio_centros_conta: rateioCentroConta || {},
    rateio_centros_conta_tp_valor: rateioCentroContaTpValor || {},
  };
}

//Carrega os dados do rateio gravados no banco em objeto rateio para edição
function carregarRateioCentroEdicao(list) {
  return list.reduce((acc, item) => {
    const idConta = item.id_conta;

    if (!acc[idConta]) {
      acc[idConta] = {
        id_conta: idConta,
        tipo_valor_conta: item.tipo_valor_conta,
        valor_conta: item.valor_conta,
        qtdes_centros: {},
        valores_centros: {},
        valor_total_centros: 0,
      };
    }

    //Soma o valor total
    acc[idConta].valor_total_centros += Number(item.valor);

    //Adiciona o valor do centro de custo
    acc[idConta].valores_centros[item.id_centro_custo] = Number(item.valor);

    //Adiciona a quantidade do centro de custo
    acc[idConta].qtdes_centros[item.id_centro_custo] = Number(item.quantidade);

    return acc;
  }, {});
}

//Verifica se as informações para realizar o rateio automático estão no sistema (fat / qtde colab)
export function fn_validarInfRateioAutomatico(
  formaRateio,
  infRateioAutomatico,
) {
  let isValido = true;

  if (infRateioAutomatico) {
    //Se for automático por valor (fat filial) e não tiver inf. de fat. em nenhuma filial
    if (
      formaRateio == FORMA_RATEIO.VALOR &&
      fn_isEmpty(infRateioAutomatico.fat_filial)
    ) {
      isValido = false;
      showMensagemErro(
        MENSAGENS_ERRO.RATEIO_SEM_INF_FAT_MES_ANO,
        infRateioAutomatico.mes_ano,
      );
    }
    //Se for automático por qtde e não tiver inf. de qtde colab em nenhum centro custo
    else if (
      formaRateio == FORMA_RATEIO.QUANTIDADE &&
      fn_isEmpty(infRateioAutomatico.qtdes_colab_centro)
    ) {
      isValido = false;
      showMensagemErro(
        MENSAGENS_ERRO.RATEIO_SEM_INF_QTDE_COLAB_MES_ANO,
        infRateioAutomatico.mes_ano,
      );
    }
  } else {
    isValido = false;
  }

  return isValido;
}

//Retorna a descrição dos centros do rateio (card Rateio da conferência): com um centro, "código - nome";
//com mais de um, a quantidade por tipo, só dos tipos que existem no rateio (Ex: "5 lojas; 2 Adm; 1 CD")
export function fn_getDescricaoCentrosRateio(centros = []) {
  const centrosDistintos = [
    ...new Map(
      centros.map((centro) => [String(centro.id ?? centro.value), centro]),
    ).values(),
  ];

  if (centrosDistintos.length == 0) return "";
  if (centrosDistintos.length == 1) {
    return fn_getLabelCentroCusto(centrosDistintos[0]);
  }

  const descricoesTipo = [
    [
      TIPO_CENTRO_CUSTO.LOJA,
      (qtde) => (qtde == 1 ? "1 loja" : `${qtde} lojas`),
    ],
    [TIPO_CENTRO_CUSTO.ADMINISTRATIVO, (qtde) => `${qtde} Adm`],
    [TIPO_CENTRO_CUSTO.CENTRO_DISTRIBUICAO, (qtde) => `${qtde} CD`],
    [TIPO_CENTRO_CUSTO.PAGAMENTO, (qtde) => `${qtde} Pag`],
  ];

  return descricoesTipo
    .map(([tipo, fnDescricao]) => {
      const qtde = centrosDistintos.filter(
        (centro) => centro.tipo == tipo,
      ).length;
      return qtde > 0 ? fnDescricao(qtde) : null;
    })
    .filter((descricao) => descricao)
    .join("; ");
}

//Ordem dos tipos de centro custo na ordenação do rateio por tipo: primeiro as lojas (centros com empresa)
const ORDEM_TIPO_CENTRO_RATEIO = [
  TIPO_CENTRO_CUSTO.LOJA,
  TIPO_CENTRO_CUSTO.ADMINISTRATIVO,
  TIPO_CENTRO_CUSTO.CENTRO_DISTRIBUICAO,
  TIPO_CENTRO_CUSTO.PAGAMENTO,
];

//Retorna a função de comparação dos centros do rateio (tela do rateio dos centros) conforme o campo e a direção
//@campoOrder: codigo, nome ou tipo (pelo tipo: loja, adm, CD e pagamento; na loja, pelo código da empresa; e depois pelo nome)
//@direcaoOrder: ASC ou DESC
//@codigosEmpresa: código de cada empresa, pelo id ({ [id_empresa]: codigo })
export function fn_getComparacaoCentrosRateio(
  campoOrder,
  direcaoOrder,
  codigosEmpresa = {},
) {
  const fnPosicaoTipo = (centro) => {
    const posicao = ORDEM_TIPO_CENTRO_RATEIO.indexOf(centro.tipo);
    return posicao < 0 ? ORDEM_TIPO_CENTRO_RATEIO.length : posicao;
  };

  //Código da empresa do centro loja (o centro sem empresa fica depois dos que têm)
  const fnCodigoEmpresa = (centro) =>
    codigosEmpresa[centro.id_empresa] ?? Number.MAX_SAFE_INTEGER;

  //Na loja, compara pelo código da empresa
  const fnCompararEmpresa = (a, b) =>
    a.tipo == TIPO_CENTRO_CUSTO.LOJA && b.tipo == TIPO_CENTRO_CUSTO.LOJA
      ? fnCodigoEmpresa(a) - fnCodigoEmpresa(b)
      : 0;

  const fnComparar = (a, b) => {
    if (campoOrder == "codigo") return a.codigo - b.codigo;
    if (campoOrder == "tipo") {
      return (
        fnPosicaoTipo(a) - fnPosicaoTipo(b) ||
        fnCompararEmpresa(a, b) ||
        a.nome.localeCompare(b.nome)
      );
    }
    return a.nome.localeCompare(b.nome);
  };

  return direcaoOrder == "DESC" ? (a, b) => fnComparar(b, a) : fnComparar;
}
